import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  useMediaQuery,
  CircularProgress,
  Popover,
} from '@mui/material';
import { BsCloudRain, BsCloudHaze } from 'react-icons/bs'; // Lluvia y Niebla
import { FiWind } from 'react-icons/fi'; // Viento
import { keyframes } from '@emotion/react';

import { useTheme } from '@mui/system';
import { UserContext } from '../../../hooks';
import { getRequiredUserDataMessage } from '../../../utils/validation';
import { toast, ToastContainer } from 'react-toastify';
import ConfirmationDialog from '../../../components/Confirmation';
import CompleteProfileDialog from '../../../components/CompleteProfileDialog';
import axiosInstance from '../../../services/axiosInstance';

export const Schedule = () => {
  const { courtId } = useParams();
  const { user, isMember } = useContext(UserContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [confirmation, setConfirmation] = useState(false);
  const [cancelTime, setCancelTime] = useState();
  const [bookId, setBookId] = useState();
  const [court, setCourt] = useState(null);
  const [courtBooked, setCourtBooked] = useState(new Map());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimes, setSelectedTimes] = useState(new Set());
  const [maxDays, setMaxDays] = useState(1);
  const [advanceBookingHour, setAdvanceBookingHour] = useState(null);
  const [days, setDays] = useState([]);
  const [timeIntervals, setTimeIntervals] = useState([]);
  const [loadingIntervals, setLoadingIntervals] = useState(true);
  const [unpaidPastBookings, setUnpaidPastBookings] = useState(0);
  const [completeProfileOpen, setCompleteProfileOpen] = useState(false);
  const [weatherData, setWeatherData] = useState([]);
  const [weatherSelected, setWeatherSelected] = useState(null);
  const [loadingWeather, setLoadingWeather] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpenPopover = (event, weather) => {
    event.stopPropagation(); // Prevenir activación del botón de reserva
    setWeatherSelected(weather);
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const flashing = keyframes`
  0%, 100% { box-shadow: 0 0 10px rgba(206, 37, 37, 0.6); }
  50% { box-shadow: 0 0 20px rgba(206, 37, 37, 1); }
`;
  // Obtener la hora de corte desde el servidor
  useEffect(() => {
    const fetchAdvanceBookingHour = async () => {
      try {
        const response = await axiosInstance.get(
          '/admin/settings/advance-booking-hour/public'
        );
        if (response.success) {
          setAdvanceBookingHour(response.data.advanceBookingHour ?? 0);
        }
      } catch (error) {
        console.error('Error al obtener hora de corte:', error);
        setAdvanceBookingHour(0); // fallback: medianoche (comportamiento original)
      }
    };
    fetchAdvanceBookingHour();
  }, []);

  // Generate the next N days (+ 1 extra si la hora actual >= hora de corte)
  useEffect(() => {
    if (advanceBookingHour === null) return; // esperar a que cargue

    const currentDate = new Date();
    const currentHour = currentDate.getHours();

    // Si la hora actual es >= hora de corte, mostrar un día extra
    const extraDay =
      advanceBookingHour > 0 && currentHour >= advanceBookingHour ? 1 : 0;
    const totalDays = maxDays + extraDay;

    const nextDays = [];

    for (let i = 0; i < totalDays; i++) {
      const nextDate = new Date(currentDate);
      nextDate.setDate(currentDate.getDate() + i);

      const dayOfMonth = nextDate.getDate();
      const dayName = [
        'Domingo',
        'Lunes',
        'Martes',
        'Miércoles',
        'Jueves',
        'Viernes',
        'Sábado',
      ][nextDate.getDay()];

      nextDays.push({
        date: new Date(nextDate.setHours(0, 0, 0, 0)), // Fecha completa ajustada a las 00:00
        dayName,
        dayOfMonth,
      });
    }

    setDays(nextDays);
  }, [maxDays, advanceBookingHour]);

  useEffect(() => {
    const fetchCourtData = async () => {
      try {
        const response = await axiosInstance.get(`/courts/${courtId}`);
        setCourt(response.court);

        const roleMaxDays = {
          member: response.court.maxDaysInAdvance.member,
          nonMember: response.court.maxDaysInAdvance.nonMember,
          uca: response.court.maxDaysInAdvance.uca,
          coach: response.court.maxDaysInAdvance.coach,
        };

        // Definir los roles que se consideran "miembros"
        const memberRoles = [
          'admin',
          'user-adult',
          'user-youth',
          'user-padel',
          'vip',
        ];

        // Verificar si el usuario es miembro
        const isMemberCustom = memberRoles.includes(user.role);

        setMaxDays(
          isMemberCustom
            ? roleMaxDays.member
            : roleMaxDays[user.role] || roleMaxDays.nonMember
        );
      } catch (error) {
        console.error('Error al obtener la pista:', error);
        toast.error('No se pudo cargar la pista.');
      }
    };

    fetchCourtData();
  }, [courtId, isMember]); // Se ejecuta solo cuando cambia la pista o el rol del usuario

  useEffect(() => {
    if (!court) return;

    const fetchTimeIntervals = async () => {
      setLoadingIntervals(true);
      try {
        const response = await axiosInstance.get(`/courts/time-intervals`, {
          params: { courtId, date: selectedDate.toISOString().split('T')[0] },
        });
        setTimeIntervals(response.timeIntervals || []);
        setSelectedTimes(new Set());
      } catch (error) {
        console.error('Error al obtener los intervalos de tiempo:', error);
        toast.error('No se pudieron cargar los horarios.');
      } finally {
        setLoadingIntervals(false);
      }
    };

    fetchTimeIntervals();
  }, [courtId, selectedDate, court]);

  useEffect(() => {
    const fetchMyBookings = async () => {
      try {
        const response = await axiosInstance.get(`/users/bookings`);
        if (response.success) {
          const now = new Date();
          let unpaidCount = 0;

          response.bookings.forEach(item => {
            // Count unpaid bookings in the past
            if (
              item.userBooked._id === user.id &&
              new Date(item.dateBooked) < now &&
              item.paymentStatus === 'C' &&
              !item.deletedAt
            ) {
              unpaidCount++;
            }
          });
          setUnpaidPastBookings(unpaidCount);
        } else {
          toast.error('Error al mostrar mis reservas.');
        }
      } catch (error) {
        console.error('Error fetching bookings:', error);
        toast.error('Error al cargar las reservas.');
      }
    };

    fetchMyBookings();
  }, [user.id]);

  useEffect(() => {
    if (maxDays === 1) return; // Evita que se ejecute con el valor inicial

    const fetchBookings = async () => {
      try {
        const today = new Date();
        const endDate = new Date();
        endDate.setDate(today.getDate() + maxDays);

        const response = await axiosInstance.get(`/users/booked`, {
          params: {
            courtId,
            isDelete: true,
            startDate: today.toISOString().split('T')[0], // YYYY-MM-DD
            endDate: endDate.toISOString().split('T')[0], // YYYY-MM-DD
          },
        });

        if (response.success) {
          const tempCourtBooked = new Map();

          response.bookings.forEach(item => {
            // Convertir `dateBooked` en el formato clave `YYYY-MM-DD`
            const dayKey = new Date(item.dateBooked).toLocaleDateString(
              'sv-SE'
            );

            // Si el `dayKey` no existe en el Map, inicializarlo con otro Map
            if (!tempCourtBooked.has(dayKey)) {
              tempCourtBooked.set(dayKey, new Map());
            }

            // Almacenar la reserva en el Map interno
            tempCourtBooked.get(dayKey).set(item.timeBooked, item);
          });

          setCourtBooked(tempCourtBooked);
        } else {
          toast.error(response.message || 'Error al cargar las reservas.');
        }
      } catch (error) {
        console.error('Error al cargar reservas:', error);
        toast.error('No se pudieron cargar las reservas.');
      }
    };

    fetchBookings();
  }, [maxDays, courtId]); // Se ejecuta solo cuando `maxDays` cambia

  // Fetch weather data
  useEffect(() => {
    const today = new Date();
    const diffDays = Math.floor(
      (selectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays >= 4) {
      setWeatherData([]); // Limpiamos datos si está fuera del rango
      return;
    }

    const fetchWeatherData = async () => {
      setLoadingWeather(true);
      try {
        const response = await axiosInstance.post(`/weather`, {
          date: selectedDate.toISOString(),
        });

        setWeatherData(response.weather || []);
      } catch (error) {
        console.error('Error al cargar el clima:', error);
        toast.error('No se pudo cargar el clima.');
      } finally {
        setLoadingWeather(false);
      }
    };

    fetchWeatherData();
  }, [selectedDate]); // Solo se ejecuta cuando cambia `selectedDate`

  const getHourlyWeather = timeIndex => {
    if (loadingWeather || !weatherData.length) return null;

    const targetTime = timeIntervals[timeIndex].split(' - ')[0];
    const [hour] = targetTime.split(':');
    return weatherData.find(interval => {
      const intervalHour = new Date(interval.startTime).getHours();
      return parseInt(hour) === intervalHour;
    })?.values;
  };

  const getBookingStartTime = booker => {
    const [startHour, startMinute] = booker.timeBooked
      .split(' - ')[0]
      .split(':'); // Split the time correctly

    if (!selectedDate) {
      console.error('Selected date is undefined');
      return null;
    }

    return new Date(
      selectedDate.getFullYear(), // Extract year
      selectedDate.getMonth(), // Extract month
      selectedDate.getDate(), // Extract day
      parseInt(startHour), // Extract hour
      parseInt(startMinute) // Extract minute
    );
  };

  const isCancelable = booker => {
    const startTime = getBookingStartTime(booker);
    if (!startTime) return { canCancel: false, reason: 'Fecha no válida' };

    const now = new Date();
    const diffMs = startTime - now;
    const diffHours = diffMs / (1000 * 3600);

    if (diffHours < 6) {
      return {
        canCancel: false,
        reason: 'Faltan menos de 6 horas para el inicio de la reserva',
      };
    }

    if (booker.paymentStatus === 'P') {
      return { canCancel: false, reason: 'La reserva ya está pagada' };
    }

    if (booker.paymentStatus === 'S') {
      return {
        canCancel: false,
        reason: 'La reserva está parcialmente pagada',
      };
    }

    return { canCancel: true };
  };

  const capitalizeFirstLetter = str =>
    str.charAt(0).toUpperCase() + str.slice(1);

  // Función para comprobar si una hora está dentro de un rango
  const isTimeInRange = (time, range) => {
    const [timeStart, timeEnd] = time.split(' - ').map(convertToMinutes);
    const [rangeStart, rangeEnd] = range.split(' - ').map(convertToMinutes);

    return timeStart >= rangeStart && timeEnd <= rangeEnd;
  };

  // Convierte una hora tipo "HH:MM" en minutos desde medianoche
  const convertToMinutes = time => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const getTimeBlockInfo = timeName => {
    if (!court?.blockedTimes || court.blockedTimes.length === 0) {
      return { isBlocked: false, reason: '' };
    }

    const selectedDay = capitalizeFirstLetter(
      selectedDate.toLocaleDateString('es-ES', {
        weekday: 'long',
      })
    );

    for (const block of court.blockedTimes) {
      const blockStart = new Date(block.startDate);
      const blockEnd = new Date(block.endDate);

      // ✅ Comprobar si la fecha seleccionada está dentro del rango de bloqueo
      if (selectedDate < blockStart || selectedDate > blockEnd) {
        continue;
      }

      // ✅ Comprobar si el día de la semana está dentro de los días bloqueados
      if (!block.daysOfWeek.includes(selectedDay)) {
        continue;
      }

      // ✅ Comprobar si el horario seleccionado está dentro del rango bloqueado
      if (!isTimeInRange(timeName, block.timeRange)) {
        continue;
      }

      // 🔥 **Verificar si el horario tiene una excepción**
      if (block.exceptions && block.exceptions.length > 0) {
        const hasException = block.exceptions.some(exception => {
          const exceptionDate = new Date(exception.date);
          return (
            exceptionDate.toDateString() === selectedDate.toDateString() &&
            isTimeInRange(timeName, exception.timeRange)
          );
        });

        if (hasException) {
          continue; // ❌ No está bloqueado porque hay una excepción
        }
      }

      return {
        isBlocked: true,
        reason: block.reason?.trim() || '',
      }; // 🔒 Está bloqueado
    }

    return { isBlocked: false, reason: '' };
  };

  const toggleTime = timeIndex => {
    const dayKey = selectedDate.toLocaleDateString('sv-SE');
    const dailyBookings = courtBooked.get(dayKey) || new Map(); // Si no hay reservas, usamos un Map vacío
    const booker = dailyBookings.get(timeIntervals[timeIndex]);

    if (!dailyBookings) {
      toast.error(`No hay reservas disponibles para el ${dayKey}`);
      return;
    }

    if (booker?.userBooked._id === user.id) {
      const { canCancel, reason } = isCancelable(booker);

      if (!canCancel) {
        toast.error(`No se puede cancelar la reserva: ${reason}`);
        return;
      }

      if (cancelTime === timeIndex) {
        setCancelTime(undefined);
      } else {
        setBookId(booker._id);
        setCancelTime(timeIndex);
      }
      return;
    }

    const requiredUserDataMessage = getRequiredUserDataMessage(user);
    if (requiredUserDataMessage) {
      setCompleteProfileOpen(true);
      return;
    }

    // Si el usuario ya tiene reservas en este día, contarlas
    let userDailyBookings = 0;
    dailyBookings.forEach(booking => {
      if (booking.userBooked._id === user.id) {
        userDailyBookings++;
      }
    });

    // Restringir a un máximo de 2 reservas por día
    if (userDailyBookings >= 2 && user.role !== 'coach') {
      toast.error('No puedes hacer más de 2 reservas en un mismo día.');
      return;
    }

    // Check unpaid past bookings
    if (unpaidPastBookings >= 3) {
      toast.error(
        'Tienes más de 3 reservas no pagadas en el pasado. Por favor, regulariza tu situación.'
      );
      return;
    }

    // Calcular el rango de 7 días desde hoy
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Asegurar que comparamos solo fechas

    const endOfRange = new Date(today);
    endOfRange.setDate(today.getDate() + 7); // Maneja cambio de mes/año

    // Contar reservas de la semana actual
    let currentWeekReservations = 0;

    courtBooked.forEach((dailyBookings, dateKey) => {
      const bookingDate = new Date(dateKey);

      if (bookingDate >= today && bookingDate < endOfRange) {
        dailyBookings.forEach(booking => {
          if (booking.userBooked._id === user.id) {
            currentWeekReservations++;
          }
        });
      }
    });

    // Validate limits based on user role
    const roleMaxDays = {
      member: court.maxBookingsPerWeek.member,
      nonMember: court.maxBookingsPerWeek.nonMember,
      uca: court.maxBookingsPerWeek.uca,
      coach: court.maxBookingsPerWeek.coach,
    };

    // Definir los roles que se consideran "miembros"
    const memberRoles = [
      'admin',
      'user-adult',
      'user-youth',
      'user-padel',
      'vip',
    ];

    // Verificar si el usuario es miembro
    const isMemberCustom = memberRoles.includes(user.role);

    const maxReservations = isMemberCustom
      ? roleMaxDays.member
      : roleMaxDays[user.role] || roleMaxDays.nonMember;

    if (selectedTimes.size + currentWeekReservations >= maxReservations) {
      if (isMember) {
        toast.error(
          `Has alcanzado el límite de ${maxReservations} reservas en los próximos 7 días.`
        );
      } else {
        toast.error(
          `Los no socios solo pueden reservar ${maxReservations} pista por semana. ¡Hazte socio!`
        );
      }

      const tempSet = new Set([...selectedTimes]);
      tempSet.delete(timeIndex);
      return;
    }

    // Add or remove the selected time
    let tempSet = new Set([...selectedTimes]);

    if (tempSet.has(timeIndex)) {
      tempSet.delete(timeIndex);
    } else if (tempSet.size < 1) {
      tempSet.add(timeIndex);
    } else {
      tempSet = new Set();
      tempSet.add(timeIndex);
    }
    setSelectedTimes(tempSet);
  };

  const handleReserve = async () => {
    const requiredDataMessage = getRequiredUserDataMessage(user);
    if (requiredDataMessage) {
      setCompleteProfileOpen(true);
      return;
    }
    setConfirmation(true);
  };

  const isPast = timeName => {
    const [startHour, startMinute] = timeName.split(' - ')[0].split(':');

    // Crear la fecha y hora completa del intervalo seleccionado
    const intervalTime = new Date(selectedDate);
    intervalTime.setHours(parseInt(startHour), parseInt(startMinute), 0, 0);

    // Comparar con la fecha y hora actual
    return intervalTime < new Date();
  };

  return (
    <Box>
      <ToastContainer />
      <Typography
        variant={isSmallScreen ? 'h6' : 'h5'}
        align="center"
        gutterBottom
      >
        Selecciona Horarios para {court?.type.toUpperCase()} - {court?.name}
      </Typography>

      {/* Day Selector */}
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="center"
        gap={1}
        mb={4}
        mt={3}
      >
        {days.map((dayObj, index) => {
          const day = dayObj.date;
          return (
            <Button
              key={index}
              variant={
                selectedDate.toLocaleDateString('sv-SE') ===
                day.toLocaleDateString('sv-SE')
                  ? 'contained'
                  : 'outlined'
              }
              onClick={() => setSelectedDate(day)}
              size={isSmallScreen ? 'small' : 'medium'}
              sx={{
                flex: '1 0 120px',
                maxWidth: '150px',
                textAlign: 'center',
              }}
            >
              {day.toLocaleDateString('es-ES', {
                weekday: 'short',
                day: 'numeric',
              })}
            </Button>
          );
        })}
      </Box>

      {/* Horarios */}
      {loadingIntervals ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={isSmallScreen ? 1 : 2}>
          {timeIntervals.map((timeName, index) => {
            const dayKey = selectedDate.toLocaleDateString('sv-SE');
            const dailyBookings = courtBooked.get(dayKey);
            // Si no hay reservas para ese día, el horario está disponible
            const booker = dailyBookings?.get(timeName) || null;
            const isBlocked = Boolean(booker);
            const isUserBooking = booker?.userBooked._id === user.id;
            const blockInfo = getTimeBlockInfo(timeName);
            const weather = getHourlyWeather(index);

            return (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Button
                  variant="contained"
                  fullWidth
                  size={isSmallScreen ? 'small' : 'medium'}
                  color={
                    isBlocked
                      ? isUserBooking
                        ? 'success'
                        : 'error'
                      : selectedTimes.has(index)
                        ? 'primary'
                        : 'default'
                  }
                  onClick={() => toggleTime(index)}
                  disabled={
                    blockInfo.isBlocked ||
                    (isBlocked && !isUserBooking) ||
                    isPast(timeName)
                  }
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    padding: 1.5,
                  }}
                >
                  {/* Mostrar hora */}
                  <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
                    {blockInfo.isBlocked
                      ? blockInfo.reason || 'Horario bloqueado'
                      : isBlocked
                        ? isUserBooking
                          ? 'Tu Reserva'
                          : isMember
                            ? `${booker.userBooked?.name || ''} ${booker.userBooked?.surname || ''}`.trim() ||
                              'Socio' // ✅ Si es miembro, mostrar nombre y apellido
                            : 'No socio' // ❌ Si no es miembro, mostrar "No socio"
                        : timeName}
                  </Typography>

                  {/* Mostrar advertencias visuales */}
                  {loadingWeather && isMember ? (
                    <CircularProgress size={20} />
                  ) : weather ? (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.8,
                        mt: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {weather.precipitationProbability >= 10 && (
                        <BsCloudRain
                          style={{
                            fontSize: '1.5rem',
                            color: '#007BFF', // Azul para lluvia
                            cursor: 'pointer',
                          }}
                          onClick={event => handleOpenPopover(event, weather)}
                        />
                      )}
                      {weather.windSpeed * 3.6 >= 25 && (
                        <FiWind
                          style={{
                            fontSize: '1.5rem',
                            color: '#FF9800', // Naranja para viento
                            cursor: 'pointer',
                          }}
                          onClick={event => handleOpenPopover(event, weather)}
                        />
                      )}
                      {weather.humidity >= 85 && (
                        <BsCloudHaze
                          style={{
                            fontSize: '1.5rem',
                            color: '#9C27B0', // Púrpura para humedad alta
                            cursor: 'pointer',
                          }}
                          onClick={event => handleOpenPopover(event, weather)}
                        />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2"></Typography>
                  )}
                </Button>

                {/* Popover para detalles del clima */}
                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClosePopover}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  PaperProps={{
                    sx: {
                      boxShadow: theme.shadows[0],
                      borderRadius: 1,
                      padding: 1,
                    },
                  }}
                >
                  <Box sx={{ textAlign: 'center', width: 120 }}>
                    {weatherSelected ? (
                      <>
                        <Typography variant="body2">
                          <strong>Temp:</strong> {weatherSelected.temperature}°C
                        </Typography>
                        <Typography variant="body2">
                          <strong>Lluvia:</strong>{' '}
                          {weatherSelected.precipitationProbability}%
                        </Typography>
                        <Typography variant="body2">
                          <strong>Viento:</strong>{' '}
                          {typeof weatherSelected.windSpeed === 'number'
                            ? (weatherSelected.windSpeed * 3.6).toFixed(1)
                            : 'N/A'}{' '}
                          km/h
                        </Typography>
                        <Typography variant="body2">
                          <strong>Humedad:</strong>{' '}
                          {weatherSelected.humidity || 'N/A'}%
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2">Sin datos</Typography>
                    )}
                  </Box>
                </Popover>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Box
        textAlign="center"
        mt={4}
        sx={{
          position: isSmallScreen ? 'fixed' : 'static', // Fijo en móviles, estático en desktop
          bottom: isSmallScreen ? 0 : 'auto', // 16px desde el borde inferior en móviles
          left: isSmallScreen ? '50%' : 'auto', // Centrado horizontalmente en móviles
          transform: isSmallScreen ? 'translateX(-50%)' : 'none', // Centrar correctamente en móviles
          width: isSmallScreen ? '100%' : 'auto', // Botón más ancho en móviles
          zIndex: 1000, // Asegura que esté por encima de otros elementos
          textAlign: 'center',
          p: isSmallScreen ? 1 : 0, // Espaciado en móviles para mejor visibilidad
          backgroundColor: isSmallScreen
            ? 'rgba(255, 255, 255, 0)'
            : 'transparent', // Fondo semi-transparente en móviles
          borderRadius: isSmallScreen ? '8px' : 'none', // Bordes redondeados en móviles
          boxShadow: isSmallScreen ? '0px 4px 10px rgba(0,0,0,0.2)' : 'none', // Sombra en móviles para destacar el botón
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size={isSmallScreen ? 'large' : 'medium'}
          onClick={handleReserve}
          disabled={selectedTimes.size === 0}
          fullWidth={isSmallScreen} // Botón a pantalla completa en móviles
        >
          Confirmar Reserva
        </Button>
      </Box>

      {/* Membership Info */}
      {!isMember && (
        <Box
          mt={4}
          sx={{
            textAlign: 'center',
            backgroundColor: '#FAE2E2', // Rojo más suave para un look más amigable
            border: '1px solid #D32F2F', // Rojo intenso para resaltar
            borderRadius: 3,
            p: 3,
            boxShadow: '0px 6px 16px rgba(211, 47, 47, 0.3)', // Sombra más sutil y elegante
            mx: 'auto',
            maxWidth: '420px',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.02)', // Efecto ligero de crecimiento
            },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#B71C1C', // Rojo oscuro para impacto visual
              fontWeight: 'bold',
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}
          >
            ¡Hazte socio y desbloquea beneficios exclusivos!
          </Typography>
          <Typography variant="body2" sx={{ color: '#5F5F5F', mb: 2 }}>
            Reserva hasta con 7 días de anticipación, accede a mejores precios y
            disfruta de promociones especiales en nuestro club.
          </Typography>
          <Button
            href="/membership"
            variant="contained"
            size="large"
            sx={{
              backgroundColor: '#D32F2F', // Rojo más vibrante
              color: '#fff',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              px: 4,
              animation: `${flashing} 1.8s infinite`,
              borderRadius: 3,
              '&:hover': {
                backgroundColor: '#B71C1C', // Rojo aún más oscuro al pasar el mouse
              },
            }}
          >
            Únete ahora
          </Button>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmation || !isNaN(cancelTime)}
        setOpen={isOpen => {
          if (!isOpen) {
            setConfirmation(false);
            setCancelTime(undefined);
          }
        }}
        mode={confirmation ? 'confirm' : 'cancel'}
        courtConfirmation={court}
        times={confirmation ? selectedTimes : undefined}
        timeIntervals={timeIntervals}
        cancelTime={cancelTime}
        setCancelTime={setCancelTime}
        bookId={bookId}
        days={days}
        selectedDate={selectedDate}
        setCourtBooked={setCourtBooked}
        setTimes={setSelectedTimes}
      />
      <CompleteProfileDialog
        open={completeProfileOpen}
        onClose={() => setCompleteProfileOpen(false)}
        onSaved={() => setCompleteProfileOpen(false)}
      />
    </Box>
  );
};
